import { logInfo } from 'setup/logger/logInfo';

type OnSwConfigReady = () => void;

type PendingWaiter = {
  resolve: () => void;
  reject: (reason?: unknown) => void;
  /** Stops the waiter's abort listener and timeout. */
  stopWaiting: () => void;
};

/** Long enough for a login and a project download; a wait that long is logged, not endless. */
export const SW_CONFIG_READY_TIMEOUT_MS = 30_000;

/** The worker was not configured in time; listeners still run once it is. */
export class SwConfigReadyTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`Service worker config not ready after ${timeoutMs}ms`);
    this.name = 'SwConfigReadyTimeoutError';
  }
}

let hasReceivedConfigSet = false;
const readySubscribers = new Set<OnSwConfigReady>();
const pendingWaiters: PendingWaiter[] = [];

function resolvePendingWaiters(): void {
  while (pendingWaiters.length > 0) {
    const waiter = pendingWaiters.shift();

    waiter?.stopWaiting();
    waiter?.resolve();
  }
}

function rejectPendingWaiters(reason?: unknown): void {
  while (pendingWaiters.length > 0) {
    const waiter = pendingWaiters.shift();

    waiter?.stopWaiting();
    waiter?.reject(reason);
  }
}

function notifyListeners(): void {
  readySubscribers.forEach((onReady) => {
    onReady();
  });
}

/**
 * Local tab state machine:
 * - logout: resetSwConfigReady() => ready=false, pending waits rejected
 * - config ack (matching requestId): notifyServiceWorkerConfigSet() => ready=true
 * - consumers:
 *   - waitForSwConfigReady: blocks until ready (or abort/reset)
 *   - runWhenSwConfigReady: executes callback once ready
 *   - requestResyncAfterResume: triggers listeners only when already ready
 */
export function notifyServiceWorkerConfigSet(): void {
  const becameReady = !hasReceivedConfigSet;

  hasReceivedConfigSet = true;
  resolvePendingWaiters();
  if (becameReady) {
    notifyListeners();
  }
}

export function resetSwConfigReady(): void {
  hasReceivedConfigSet = false;
  rejectPendingWaiters(new Error('SW config ready state reset'));
}

export function subscribeSwConfigReady(
  onConfigReady: OnSwConfigReady
): () => void {
  readySubscribers.add(onConfigReady);

  return () => {
    readySubscribers.delete(onConfigReady);
  };
}

/**
 * Resolves once the worker is configured. Rejects on abort, on logout
 * (`resetSwConfigReady`) and after `timeoutMs`; consumers that must run once
 * ready also subscribe with `subscribeSwConfigReady`.
 */
export function waitForSwConfigReady(
  signal?: AbortSignal,
  timeoutMs: number = SW_CONFIG_READY_TIMEOUT_MS
): Promise<void> {
  if (hasReceivedConfigSet) {
    return Promise.resolve();
  }

  if (signal?.aborted) {
    return Promise.reject(signal.reason);
  }

  return new Promise<void>((resolve, reject) => {
    const removeWaiter = (): void => {
      const index = pendingWaiters.indexOf(waiter);
      if (index >= 0) {
        pendingWaiters.splice(index, 1);
      }
    };
    const onAbort = (): void => {
      removeWaiter();
      stopWaiting();
      reject(signal?.reason);
    };
    const timer = setTimeout(() => {
      removeWaiter();
      stopWaiting();
      reject(new SwConfigReadyTimeoutError(timeoutMs));
    }, timeoutMs);
    const stopWaiting = (): void => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
    };

    const waiter: PendingWaiter = { resolve, reject, stopWaiting };
    signal?.addEventListener('abort', onAbort, { once: true });
    pendingWaiters.push(waiter);
  });
}

export function runWhenSwConfigReady(
  onReady: () => void,
  signal?: AbortSignal
): void {
  void waitForSwConfigReady(signal)
    .then(() => {
      if (signal?.aborted) {
        return;
      }

      onReady();
    })
    .catch((error: unknown) => {
      if (error instanceof SwConfigReadyTimeoutError) {
        logInfo('sw-config-ready-timeout', { timeoutMs: SW_CONFIG_READY_TIMEOUT_MS });
      }
    });
}

/**
 * Focus/visibility path: run resync only when config is already acknowledged.
 */
export function requestResyncAfterResume(): void {
  if (!hasReceivedConfigSet) {
    return;
  }

  notifyListeners();
}

/** Test-only reset */
export function resetSwConfigReadyStateForTests(): void {
  hasReceivedConfigSet = false;
  readySubscribers.clear();
  pendingWaiters.length = 0;
}
